import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '../stores/auth';
import { supabase, API } from '../lib/supabase';
import { Shield, Lock, User, AlertCircle, Loader, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import { phiText } from '../lib/phiConfig';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/components/ui/input-otp';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [locked, setLocked] = useState(false);
  const [challenge, setChallenge] = useState(null);
  const [required, setRequired] = useState([]);
  const [attributes, setAttributes] = useState({});
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [code, setCode] = useState('');
  const [factor, setFactor] = useState(null);
  const timer = useRef(null);
  const errorRef = useRef(null);
  const signIn = useAuth(s => s.signIn);
  const nav = useNavigate();
  const [params] = useSearchParams();
  const reason = params.get('reason');
  useEffect(() => () => clearTimeout(timer.current), []);
  useEffect(() => { if (error) errorRef.current?.focus(); }, [error]);

  const record = (path) => fetch(`${API}/auth/${path}`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ email: email.trim() }),
  });
  const lockAccount = () => {
    setLocked(true);
    timer.current = setTimeout(() => nav('/forgot-password?reason=locked', { replace: true, state: { identifier: email.trim() } }), 3000);
  };
  const startMfaSetup = async () => {
    const { data, error } = await supabase.auth.mfa.enroll({ factorType: 'totp', friendlyName: 'Operon' });
    if (error) throw error;
    setFactor(data);
  };
  const acceptResult = async (data) => {
    setPassword('');
    setNext('');
    setConfirm('');
    setCode('');
    if (data.challenge) {
      setChallenge(data.challenge);
      setRequired(data.requiredAttributes || []);
      if (data.challenge === 'MFA_SETUP') await startMfaSetup();
      return;
    }
    await useAuth.getState().loadProfile(data.user);
    await record('record-successful-login').catch(() => {});
    toast.success('Welcome back');
    nav('/', { replace: true });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting || locked) return;
    setError(null);
    setSubmitting(true);
    try {
      const statusRes = await record('login-status');
      if (!statusRes.ok) throw new Error('Unable to check sign-in status. Please try again.');
      if ((await statusRes.json()).locked) { lockAccount(); return; }
      await acceptResult(await signIn(email.trim(), password));
    } catch (err) {
      if (err.code === 'PasswordResetRequiredException' || err.name === 'PasswordResetRequiredException') {
        nav('/forgot-password', { state: { identifier: email.trim() } });
        return;
      }
      // Infrastructure/profile/MFA errors must not consume password attempts.
      if (err.code === 'INVALID_CREDENTIALS') {
        let remaining;
        try {
          const response = await record('record-failed-login');
          if (response.ok) {
            const failure = await response.json();
            if (failure.locked) { lockAccount(); return; }
            remaining = failure.attempts_remaining;
          }
        } catch (_) { /* Cognito also enforces its own throttling */ }
        setError(`${err.message}${remaining > 0 && remaining < 5 ? ` ${remaining} attempts remaining before your account is locked.` : ''}`);
      } else {
        setError(err.message || 'Unable to sign in. Please try again.');
      }
    } finally { setSubmitting(false); }
  };

  const submitChallenge = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setError(null);
    setSubmitting(true);
    try {
      let result;
      if (challenge === 'NEW_PASSWORD_REQUIRED') {
        if (next !== confirm) throw new Error('Passwords do not match.');
        result = await supabase.auth.completeNewPassword(next, attributes);
      } else if (challenge === 'MFA_SETUP') {
        const ch = await supabase.auth.mfa.challenge({ factorId: factor.id });
        if (ch.error) throw ch.error;
        result = await supabase.auth.mfa.verify({ factorId: factor.id, challengeId: ch.data.id, code });
      } else result = await supabase.auth.confirmSignIn(code);
      if (result.error) throw result.error;
      await acceptResult(result.data);
    } catch (err) { setError(err.message || 'Verification failed. Please try again.'); }
    finally { setSubmitting(false); }
  };
  const cancelChallenge = () => {
    supabase.auth.cancelChallenge();
    setChallenge(null); setFactor(null); setCode(''); setNext(''); setConfirm(''); setAttributes({}); setError(null);
  };
  const passwordOk = next.length >= 12 && /[A-Z]/.test(next) && /[a-z]/.test(next) && /\d/.test(next) && /[^A-Za-z0-9]/.test(next) && next === confirm;

  return (
    <div className="min-h-screen flex flex-col bg-[var(--color-warm-white)] header-wash">
      <div className="flex-1 flex items-center justify-center px-4 py-10">
        <div className="w-full max-w-md fade-up">
          <div className="flex flex-col items-center mb-6">
            <img src="/om-icon.png" alt="Operon Management" className="h-24 w-24 sm:h-28 sm:w-28 object-contain" />
            <h1 className="font-display text-3xl sm:text-4xl font-semibold mt-4 text-[var(--color-ink)] tracking-tight" data-testid="login-brand">Operon</h1>
            <p className="text-xs text-[var(--color-muted)] uppercase tracking-[0.18em] mt-1.5">Practice Management</p>
            <p className="text-[10.5px] text-[var(--color-muted)] mt-0.5 italic">Optimizing Healthcare Operations</p>
          </div>
          <form onSubmit={challenge ? submitChallenge : handleSubmit} className="to-card p-6 sm:p-7 space-y-4" data-testid={challenge ? 'login-challenge-form' : 'login-form'} aria-busy={submitting}>
            <div>
              <h2 data-testid="login-title" className="font-display text-xl font-semibold text-[var(--color-ink)]">{challenge === 'NEW_PASSWORD_REQUIRED' ? 'Set your new password' : challenge ? 'Verify your sign-in' : 'Sign in to your practice'}</h2>
              <p data-testid="login-description" className="text-sm text-[var(--color-muted)] mt-1">{challenge === 'NEW_PASSWORD_REQUIRED' ? 'Replace your temporary password to activate your invitation.' : challenge === 'MFA_SETUP' ? 'Set up your authenticator to finish signing in.' : challenge === 'SMS_MFA' ? 'Enter the 6-digit code sent to your phone.' : challenge ? 'Enter the 6-digit code from your authenticator app.' : phiText('HIPAA-secured, BAA-ready platform.', 'Secure, BAA-ready platform.')}</p>
            </div>
            {!challenge && reason === 'inactivity' && <Alert data-testid="login-inactivity"><AlertCircle size={16} /><AlertDescription>You’ve been signed out due to inactivity. Please sign in again.</AlertDescription></Alert>}
            {!challenge && reason === 'reset' && <Alert data-testid="login-reset-success"><AlertDescription>Password reset. Sign in with your new password.</AlertDescription></Alert>}
            {!challenge && <>
              <label className="block">
                <span className="text-sm font-medium text-[var(--color-slate)] block mb-1.5">Email or username</span>
                <div className="relative"><User size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--color-muted)]" />
                  <Input data-testid="login-userid-input" type="text" autoCapitalize="none" autoCorrect="off" spellCheck={false} autoFocus required className="input !pl-9 h-11" value={email} onChange={e => setEmail(e.target.value)} placeholder="Email or User ID" autoComplete="username" aria-label="Email or username" disabled={submitting || locked} />
                </div>
              </label>
              <div>
                <div className="flex items-center justify-between mb-1.5"><label htmlFor="login-password" className="text-sm font-medium text-[var(--color-slate)]">Password</label><Link to="/forgot-password" state={{ identifier: email.trim() }} data-testid="login-forgot-link" className="text-xs text-[var(--color-teal-deep)] hover:underline font-medium py-2">Forgot password?</Link></div>
                <div className="relative"><Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--color-muted)]" />
                  <Input id="login-password" data-testid="login-password-input" type={showPassword ? 'text' : 'password'} required className="input !pl-9 !pr-12 h-11" value={password} onChange={e => setPassword(e.target.value)} placeholder="Password" autoComplete="current-password" disabled={submitting || locked} />
                  <Button type="button" variant="ghost" size="icon" onClick={() => setShowPassword(s => !s)} className="absolute right-0 top-0 h-11 w-11 text-[var(--color-muted)]" aria-label={showPassword ? 'Hide password' : 'Show password'} aria-pressed={showPassword} data-testid="login-password-toggle">{showPassword ? <EyeOff size={16} /> : <Eye size={16} />}</Button>
                </div>
              </div>
            </>}
            {challenge === 'NEW_PASSWORD_REQUIRED' && <>
              {required.map(name => <label key={name} className="block text-sm">{name.replace(/_/g, ' ')}<Input data-testid={`login-required-${name}`} className="input h-11 mt-1" required value={attributes[name] || ''} onChange={e => setAttributes(a => ({ ...a, [name]: e.target.value }))} /></label>)}
              <label className="block text-sm">New password<Input data-testid="auth-new-password-input" className="input h-11 mt-1" type="password" autoComplete="new-password" required autoFocus value={next} onChange={e => setNext(e.target.value)} /></label>
              <label className="block text-sm">Confirm new password<Input data-testid="auth-new-password-confirm-input" className="input h-11 mt-1" type="password" autoComplete="new-password" required value={confirm} onChange={e => setConfirm(e.target.value)} /></label>
              <p data-testid="login-password-rules" className="text-xs text-[var(--color-muted)]">At least 12 characters with uppercase, lowercase, a number and a special character.</p>
            </>}
            {challenge === 'MFA_SETUP' && (factor ? <div className="space-y-3" data-testid="login-mfa-setup"><img alt="Scan in your authenticator app" src={factor.totp.qr_code} className="h-44 w-44 mx-auto" data-testid="login-mfa-qr" /><p className="text-xs break-all text-[var(--color-muted)]" data-testid="login-mfa-secret">Or enter manually: {factor.totp.secret}</p></div> : <Button type="button" disabled={submitting} data-testid="login-mfa-retry" onClick={async () => { setSubmitting(true); try { await startMfaSetup(); setError(null); } catch (e) { setError(e.message); } finally { setSubmitting(false); } }}>Set up authenticator</Button>)}
            {challenge && challenge !== 'NEW_PASSWORD_REQUIRED' && <div className="space-y-2"><label htmlFor="login-code" className="text-sm">Verification code</label><InputOTP id="login-code" data-testid="login-mfa-code" maxLength={6} pattern="^[0-9]*$" inputMode="numeric" autoComplete="one-time-code" value={code} onChange={setCode} autoFocus><InputOTPGroup>{Array.from({ length: 6 }, (_, i) => <InputOTPSlot className="h-11" key={i} index={i} />)}</InputOTPGroup></InputOTP></div>}
            {locked && <Alert data-testid="login-locked-banner"><AlertCircle size={16} /><AlertDescription>Your account is locked. Redirecting to password reset…<Link to="/forgot-password?reason=locked" state={{ identifier: email.trim() }} data-testid="login-locked-reset-link" className="block underline mt-2">Reset password now</Link></AlertDescription></Alert>}
            {error && <Alert variant="destructive" ref={errorRef} tabIndex={-1} data-testid="login-error" aria-live="polite"><AlertCircle size={16} /><AlertDescription>{error}</AlertDescription></Alert>}
            <Button data-testid={challenge === 'NEW_PASSWORD_REQUIRED' ? 'auth-new-password-submit-button' : challenge ? 'login-mfa-submit' : 'login-submit'} type="submit" disabled={submitting || locked || (challenge === 'NEW_PASSWORD_REQUIRED' && !passwordOk) || (challenge && challenge !== 'NEW_PASSWORD_REQUIRED' && (code.length !== 6 || (challenge === 'MFA_SETUP' && !factor)))} className="btn btn-primary w-full min-h-11">{submitting && <Loader size={16} className="animate-spin" />}{submitting ? 'Please wait…' : challenge === 'NEW_PASSWORD_REQUIRED' ? 'Set new password' : challenge ? 'Verify and sign in' : locked ? 'Account locked' : 'Sign in'}</Button>
            {challenge ? <Button type="button" variant="ghost" className="w-full min-h-11" data-testid="login-challenge-cancel" disabled={submitting} onClick={cancelChallenge}>Back to sign in</Button> : <p data-testid="login-invite-notice" className="text-center text-xs text-[var(--color-muted)] pt-1">Accounts are created by invitation. Contact your practice administrator for access.</p>}
          </form>
          <p className="text-center text-xs text-[var(--color-muted)] mt-6"><Shield size={12} className="inline -mt-0.5 mr-1" />Encrypted in transit & at rest{phiText(' — HIPAA aligned', '')}</p>
        </div>
      </div>
    </div>
  );
}
